import React, { useEffect, useMemo } from "react"
import { Container, Button } from "reactstrap"
import { getOrders } from "../../api"
import TableContainer from "../../components/Common/TableContainer"
import { withTranslation } from "react-i18next"
import PropTypes from "prop-types"

function OrdersPage({ t }) {
  const [orders, setOrders] = React.useState([])
  const [filteredOrders, setFilteredOrders] = React.useState([])
  const [searchTerm, setSearchTerm] = React.useState("")
  const [activeFilter, setActiveFilter] = React.useState(null)

  // Helper functions for date filtering
  const isToday = (timestamp) => {
    const today = new Date()
    const date = new Date(timestamp)
    return date.toDateString() === today.toDateString()
  }

  const isYesterday = (timestamp) => {
    const yesterday = new Date()
    yesterday.setDate(yesterday.getDate() - 1)
    const date = new Date(timestamp)
    return date.toDateString() === yesterday.toDateString()
  }

  useEffect(() => {
    getOrders().then(res => {
      console.log(res)
      setOrders(res)
      // Set today filter by default
      const todayOrders = res.filter(order => isToday(order.createdDate))
      setFilteredOrders(todayOrders)
      setActiveFilter("today")
    })
  }, [])

  // Function to parse date from search term
  const parseDateFromSearch = (searchValue) => {
    // Match formats: DD.MM.YY or DD.MM
    const datePattern = /^(\d{1,2})\.(\d{1,2})(?:\.(\d{2}|\d{4}))?$/
    const match = searchValue.match(datePattern)

    if (!match) return null

    const day = parseInt(match[1], 10)
    const month = parseInt(match[2], 10) - 1 // months are 0-indexed
    let year = match[3] ? parseInt(match[3], 10) : new Date().getFullYear()

    // Convert 2-digit year to 4-digit year
    if (match[3] && match[3].length === 2) {
      year = 2000 + year
    }

    return { day, month, year }
  }

  // Function to check if two dates match (considering only day, month, year)
  const isSameDate = (date1, date2, includeYear = true) => {
    const d1 = new Date(date1)
    const d2 = new Date(date2)

    if (includeYear) {
      return (
        d1.getDate() === date2.day &&
        d1.getMonth() === date2.month &&
        d1.getFullYear() === date2.year
      )
    } else {
      return (
        d1.getDate() === date2.day &&
        d1.getMonth() === date2.month
      )
    }
  }

  const handleQuickFilter = (filter) => {
    setActiveFilter(filter)
    setSearchTerm("")

    if (filter === "today") {
      const filtered = orders.filter(order => isToday(order.createdDate))
      setFilteredOrders(filtered)
    } else if (filter === "yesterday") {
      const filtered = orders.filter(order => isYesterday(order.createdDate))
      setFilteredOrders(filtered)
    } else {
      setFilteredOrders(orders)
    }
  }

  // Filter orders when search term changes
  useEffect(() => {
    if (!searchTerm) {
      if (activeFilter) {
        handleQuickFilter(activeFilter)
      } else {
        setFilteredOrders(orders)
      }
      return
    }

    // Clear active filter when searching
    setActiveFilter(null)
    const parsedDate = parseDateFromSearch(searchTerm)

    if (parsedDate) {
      // Filter by date
      const includeYear = searchTerm.split('.').length === 3
      const filtered = orders.filter(order => {
        if (!order.createdDate) return false
        return isSameDate(order.createdDate, parsedDate, includeYear)
      })
      setFilteredOrders(filtered)
    } else {
      // Use default search behavior (filter by all text fields)
      const searchLower = searchTerm.toLowerCase()
      const filtered = orders.filter(order => {
        return (
          (order.orderNumber && order.orderNumber.toString().toLowerCase().includes(searchLower)) ||
          (order.fullName && order.fullName.toLowerCase().includes(searchLower)) ||
          (order.orderType && order.orderType.toLowerCase().includes(searchLower)) ||
          (order.status && order.status.toLowerCase().includes(searchLower)) ||
          (order.userId && order.userId.toString().toLowerCase().includes(searchLower)) ||
          (order.amlRiskStatus && order.amlRiskStatus.toLowerCase().includes(searchLower))
        )
      })
      setFilteredOrders(filtered)
    }
  }, [searchTerm, orders])

  document.title = t("Orders")
  const columns = useMemo(
    () => [
      {
        header: t("Number"),
        accessorKey: "orderNumber",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          const order = row.original
          return (
            <span>
              {order.orderNumber}
              {order.wasRefunded && (
                <i
                  className="mdi mdi-alert-circle text-warning ms-1"
                  title={t("This order was refunded and re-edited")}
                  style={{ fontSize: '1.2em' }}
                ></i>
              )}
            </span>
          )
        },
      },
      {
        header: t("AML status"),
        accessorKey: "amlRiskStatus",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ cell }) => {
          const amlRiskStatus = cell.getValue()
          const backgroundColor =
            amlRiskStatus === "low"
              ? "green"
              : amlRiskStatus === "medium"
              ? "orange"
              : amlRiskStatus === "high"
              ? "red"
              : "gray"

          return (
            <span
              style={{
                backgroundColor,
                color: "white",
                padding: "2px 5px",
                borderRadius: "3px",
              }}
            >
              {amlRiskStatus
                ? t(amlRiskStatus.charAt(0).toUpperCase() + amlRiskStatus.slice(1))
                : t("unknown")}
            </span>
          )
        },
      },
      {
        header: t("Name"),
        accessorKey: "fullName",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ cell, row }) => {
          const fullName = cell.getValue()
          const exchange = row.original.exchange
          return exchange || fullName || "-"
        },
      },
      {
        header: t("Source"),
        accessorKey: "exchange",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ cell }) => {
          const exchange = cell.getValue()
          return exchange ? (
            <span
              style={{
                backgroundColor: "#007bff",
                color: "white",
                padding: "2px 8px",
                borderRadius: "3px",
                fontSize: "0.85em",
              }}
            >
              {exchange}
            </span>
          ) : (
            <span
              style={{
                backgroundColor: "#6c757d",
                color: "white",
                padding: "2px 8px",
                borderRadius: "3px",
                fontSize: "0.85em",
              }}
            >
              {t("User")}
            </span>
          )
        },
      },
      {
        header: t("Created At"),
        accessorKey: "createdDate",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ cell }) => {
          const date = new Date(cell.getValue())
          return date.toLocaleDateString() + " " + date.toLocaleTimeString()
        },
      },

      {
        header: t("Type"),
        accessorKey: "orderType",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ cell }) => {
          const orderType = cell.getValue()
          return orderType.toUpperCase()
        },
      },
      {
        header: t("Status"),
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ cell }) => {
          const status = cell.getValue()
          let bgColor = "transparent"
          if (status === "finished") {
            bgColor = "green"
          } else if (status === "created") {
            bgColor = "orange"
          } else if (status === "cancelled") {
            bgColor = "red"
          } else if (status === "expired") {
            bgColor = "blue"
          }
          return (
            <span
              style={{
                backgroundColor: bgColor,
                padding: "0.2em 0.5em",
                borderRadius: "4px",
                color: "white",
              }}
            >
              {t(status)}
            </span>
          )
        },
      },
      {
        header: t("User ID"),
        accessorKey: "userId",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: t("Actions"),
        id: "actions",
        cell: ({ row }) => (
          <button
            className="btn btn-sm btn-primary"
            onClick={() => openOrderInNewTab(row)}
          >
            {t("View")}
          </button>
        ),
      },
    ],
    [t]
  )
  function openOrderInNewTab(row) {
    console.log(row)
    const orderId = row.original.id
    const orderUrl = `/orders/${orderId}`
    window.open(orderUrl, "_blank")
  }
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <h2>{t("Orders")}</h2>
          <div className="mb-3 d-flex gap-2 align-items-start">
            <input
              type="text"
              className="form-control search-box"
              placeholder={`${t("Search by date (DD.MM.YY or DD.MM) or any field")}... ${orders.length} ${t("records")}`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ maxWidth: '500px' }}
            />
            <Button
              color="primary"
              style={{
                backgroundColor: activeFilter === "today" ? "#556ee6" : "#e3e7f7",
                borderColor: activeFilter === "today" ? "#556ee6" : "#e3e7f7",
                color: activeFilter === "today" ? "white" : "#556ee6"
              }}
              onClick={() => handleQuickFilter("today")}
            >
              {t("Today")}
            </Button>
            <Button
              color="primary"
              style={{
                backgroundColor: activeFilter === "yesterday" ? "#556ee6" : "#e3e7f7",
                borderColor: activeFilter === "yesterday" ? "#556ee6" : "#e3e7f7",
                color: activeFilter === "yesterday" ? "white" : "#556ee6"
              }}
              onClick={() => handleQuickFilter("yesterday")}
            >
              {t("Yesterday")}
            </Button>
            <Button
              color="primary"
              style={{
                backgroundColor: !activeFilter ? "#556ee6" : "#e3e7f7",
                borderColor: !activeFilter ? "#556ee6" : "#e3e7f7",
                color: !activeFilter ? "white" : "#556ee6"
              }}
              onClick={() => handleQuickFilter(null)}
            >
              {t("All time")}
            </Button>
          </div>
          {(searchTerm || activeFilter) && (
            <small className="text-muted d-block mb-2">
              {t("Showing")} {filteredOrders.length} {t("of")} {orders.length} {t("orders")}
            </small>
          )}
          <TableContainer
            handleUserClick={openOrderInNewTab}
            columns={columns}
            data={filteredOrders || []}
            isGlobalFilter={false}
            isCustomPageSize={true}
            isPagination={true}
            pagination="pagination"
            paginationWrapper="dataTables_paginate paging_simple_numbers"
            tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          />{" "}
        </Container>
      </div>
    </React.Fragment>
  )
}

OrdersPage.propTypes = {
  t: PropTypes.func.isRequired,
}

export default withTranslation()(OrdersPage)
